import { yupResolver } from '@hookform/resolvers/yup';
import { Package, SlotType } from '@thurstech/commons/dist/entity/venue';
import { getPrettyDateString } from '@thurstech/commons/dist/utils/util';
import _ from 'lodash';
import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';

import SlotModal from './SlotModal';
import { useAppDispatch } from '../../../hooks/store';
import { getPackageOverridesThunk } from '../../../redux/reducer/listingSlice';
import {
  SlotsFormData,
  slotsSchema
} from '../../../validation/operations/addOrEditProduct/ticketBooking/slotsSchema';

interface SlotsProps {
  listingId: string;
  venueId: string;
  slots?: SlotType[];
  packages: Package[];
  isResourcesLinkedToVenue?: boolean;
}

export const Slots: React.FC<SlotsProps> = ({
  listingId,
  venueId,
  isResourcesLinkedToVenue,
  slots: propSlots,
  packages: propPackages
}) => {
  const [slotModalIndex, setSlotModalIndex] = useState<undefined | number>(undefined);
  const [slotsPreviousState, setSlotPreviousState] = useState<SlotType[]>([]);
  const [packages, setPackages] = useState<Package[]>(propPackages);
  const dispatch = useAppDispatch();

  useEffect(() => {
    const slotId = slotModalIndex !== undefined ? propSlots?.at(slotModalIndex)?.id : null;
    if (slotId) {
      (async () => {
        const packageOverrides = await dispatch(
          getPackageOverridesThunk({ listingId, venueId, slotId })
        ).unwrap();

        const overridesMap = _.keyBy(packageOverrides.overrides, 'packageId');
        setPackages(
          propPackages?.map((pkg, ind) => {
            const overrideDetail = overridesMap[pkg.id ?? '']?.detail;
            return overrideDetail ? { ...pkg, ...overrideDetail } : pkg;
          })
        );
      })();
    }
  }, [slotModalIndex, packages.length]);

  const {
    register,
    handleSubmit,
    getValues,
    formState: { errors, dirtyFields },
    setValue,
    watch,
    resetField,
    trigger
  } = useForm({
    resolver: yupResolver(slotsSchema),
    values: { slots: propSlots, packageOverrides: packages } as SlotsFormData,
    context: { isResourcesLinkedToVenue }
  });

  const slots = watch('slots') ?? [];
  const handleSlotModelOpen = (index: number) => {
    setSlotPreviousState(_.cloneDeep(slots));
    setSlotModalIndex(index);
  };

  const slotsError = _.get(errors, 'slots')?.message as string;
  return (
    <>
      <div className="my-4 space-y-4">
        <div className="flex justify-between">
          <h2 className="block font-bold text-xl">Slots</h2>
          <button
            onClick={() => handleSlotModelOpen(slots.length)}
            className="border-2 border-black bg-gray-200 rounded-md px-5 py-2"
            type="button"
          >
            Add Slot
          </button>
        </div>
        <div className="bg-white rounded grid grid-cols-3 gap-4">
          {slots?.map((slot, index) => (
            <div
              key={index}
              className=" bg-[#f6f6f6] p-4 rounded relative"
              role="button"
              tabIndex={0}
              aria-label={`Edit Slot ${slot.id}`}
              onClick={() => handleSlotModelOpen(index)}
              onKeyDown={e => {
                if (e.key === 'Enter' || e.key === ' ') {
                  handleSlotModelOpen(index);
                }
              }}
            >
              {getPrettyDateString(slot.sessions?.at(0)?.at(0))}
            </div>
          ))}
        </div>
        {slotsError && <p className="text-red-600 text-sm font-semibold mt-1">{slotsError}</p>}
      </div>
      <SlotModal
        isOpen={slotModalIndex !== null}
        resetState={() => setValue('slots', slotsPreviousState as SlotsFormData['slots'])}
        onClose={() => {
          setSlotModalIndex(undefined);
          resetField('packageOverrides');
        }}
        index={slotModalIndex}
        listingId={listingId!}
        venueId={venueId}
        isResourcesLinkedToVenue={isResourcesLinkedToVenue}
        {...{ register, errors, trigger, setValue, watch, getValues, dirtyFields }}
      />
    </>
  );
};
